import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Button, Collapse, Divider, Flex, Spinner, Text } from 'core/components';
import Page, { NAV_HEIGHT } from 'app/components/page/Page';
import storeLoader from 'app/stores/storeLoader';
import { BiHistory } from 'react-icons/bi';
import SudoTag from 'app/components/SudoTag';
import { TbLayoutSidebarRightCollapse } from 'react-icons/tb';
import { InputGroup } from 'core/form';
import KaizenChatSession from './KaizenChatSession';
import KaizenInput from './KaizenInput';
import KaizenChatList from './KaizenChatList';

import KaizenToolResultPanel from './KaizenToolResultPanel';
import {
  ChatLayout,
  FancyInput,
  KaizenLayout,
  KaizenScrollArea,
  KaizenSidebar,
  LargeHeading,
  SmallHeading
} from './KaizenChatSession.styles';

const INPUT_HEIGHT = 110;

@storeLoader('$users')
@inject('$auth', '$companySettings', '$journeys')
@withRouter
@observer
class KaizenMain extends Component {
  state = {
    isMyJourneysOpen: true,
    isCompanyJourneysOpen: true,
    isScrolledToBottom: false,
    isSidebarCollapsed: true,
    loading: true,
    selectedToolResults: [],
    isExistingChatLoading: false,
    searchInputValue: ''
  };

  inputRef = React.createRef();

  scrollAreaRef = React.createRef();

  componentDidMount() {
    const { history, match, $journeys } = this.props;
    const { sessionId } = match.params;

    if (this.hasAi) {
      $journeys.chatSessionCollection.fetch().then(() => {
        this.setState({ loading: false }, () => {
          if ($journeys.chatSessionCollection.size > 0) {
            if (sessionId) {
              const journey = $journeys.chatSessionCollection.get(sessionId);
              if (journey) {
                this.handleSelectJourney(journey);
              } else {
                // journey not found
                history.push($journeys.baseUrl);
              }
            }
          }
          this.scrollToBottom();
        });
      });
    }
  }

  handleEmptyStart = () => {
    const { history, $journeys } = this.props;
    $journeys.chatSessionCollection?.unselectAll();
    this.setState({
      selectedToolResults: [],
      isExistingChatLoading: false
    });
    history.push($journeys.baseUrl);
  };

  componentWillUnmount() {
    const { $journeys } = this.props;
    $journeys.chatSessionCollection.models.forEach((model) => model.unsubscribe());
    $journeys.chatSessionCollection.clearFilters();
    $journeys.chatSessionCollection.clearSelection();
  }

  componentDidUpdate(prevProps) {
    const { history, match, $journeys } = this.props;
    const { match: prevMatch } = prevProps;

    if (match.params.sessionId && match.params.sessionId !== prevMatch.params.sessionId) {
      const journey = $journeys.chatSessionCollection.get(match.params.sessionId);
      if (journey) {
        this.handleSelectJourney(journey);
      } else {
        // journey not found
        history.push($journeys.baseUrl);
      }
    }
  }

  handleScroll = () => {
    const { scrollHeight, scrollTop } = this.scrollAreaRef.current;
    const scrollAreaHeight = this.scrollAreaRef.current.clientHeight;

    //  Math.abs(... <= 1) → Checks if the difference is very small (≤ 1px) to account for rounding errors in different browsers.
    const isBottom = Math.abs(scrollHeight - Math.ceil(scrollTop) - scrollAreaHeight) <= 1;
    this.setState({ isScrolledToBottom: isBottom });
  };

  scrollToBottom = () => {
    if (this.scrollAreaRef?.current) {
      setTimeout(() => {
        const offsetBottom = this.scrollAreaRef.current.scrollTop + this.scrollAreaRef.current.scrollHeight + 100;
        this.scrollAreaRef.current.scrollTo({ top: offsetBottom, behavior: 'smooth' });
      }, 350);
    }
  };

  handleCreateJourney = () => {
    const { $journeys } = this.props;
    const newJourney = $journeys.createJourney();
    this.setState({ isExistingChatLoading: false });
    return newJourney;
  };

  handleSelectJourneyAfterCreation = (journey) => {
    const { history, $journeys } = this.props;
    history.push(`${$journeys.baseUrl}/${journey.id}`);
  };

  handleToggleMyJourneys = () => {
    this.setState((prevState) => {
      const { isMyJourneysOpen } = prevState;
      return { isMyJourneysOpen: !isMyJourneysOpen };
    });
  };

  handleToggleCompanyJourneys = () => {
    this.setState((prevState) => {
      const { isCompanyJourneysOpen } = prevState;
      return { isCompanyJourneysOpen: !isCompanyJourneysOpen };
    });
  };

  handleSearchInputChange = (e) => {
    const { $journeys } = this.props;
    const { value = '' } = e.target;
    this.setState({ searchInputValue: value });
    $journeys.chatSessionCollection.searchChats(value);
  };

  handleSelectJourney = (journey) => {
    const { history, $journeys } = this.props;
    this.setState({
      selectedToolResults: [],
      isExistingChatLoading: true
    });
    history.push(`${$journeys.baseUrl}/${journey.id}`);
    journey.selectAndSubscribe({ fetch: true });
  };

  handleCloseToolResults = () => {
    this.setState({ selectedToolResults: [] });
  };

  handleSidebarCollapse = () => {
    const { isSidebarCollapsed } = this.state;
    this.setState({ isSidebarCollapsed: !isSidebarCollapsed });
  };

  renderLoading = () => (
    <Flex
      width="100%"
      height="100%"
      flex={1}
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      bg="decks.background"
      pt={0}
      overflow="auto"
    >
      <Spinner />
    </Flex>
  );

  handleViewPrompt() {
    window.open('/api/ui/kaizen/context', '_blank');
  }

  handleCloseToolResult = (tool) => {
    this.setState((prevState) => {
      const selectedToolResults = prevState.selectedToolResults.filter((t) => t.id !== tool.id);
      return { selectedToolResults };
    });
  };

  handleSelectToolResult = (tool) => {
    this.setState((prevState) => {
      const toolExists = prevState.selectedToolResults.some((existingTool) => existingTool.id === tool.id);
      const updates = {};
      if (!toolExists) {
        updates.selectedToolResults = [...prevState.selectedToolResults, tool];
      }
      if (!prevState.isSidebarCollapsed) {
        updates.isSidebarCollapsed = true;
      }
      return Object.keys(updates).length > 0 ? updates : null;
    });
  };

  get hasAi() {
    const { $companySettings } = this.props;
    return !!$companySettings.enableKentikAi;
  }

  render() {
    const { $auth, $journeys, $companySettings, loading: loadingStores } = this.props;
    const {
      loading,
      selectedToolResults,
      isCreatingJourney,
      isSidebarCollapsed,
      isCompanyJourneysOpen,
      isMyJourneysOpen,
      isScrolledToBottom,
      isExistingChatLoading,
      searchInputValue
    } = this.state;

    const { isSudoer } = $auth;
    const containsQueries =
      $journeys.chatSessionCollection?.selected?.id && $journeys.chatSessionCollection?.selected?.get('messages');
    const selectedJourney = $journeys.chatSessionCollection?.selected;

    let title = '';
    if (selectedJourney) {
      title = selectedJourney.get('session_title');
    }

    return (
      <Page
        pt={0}
        pb={0}
        title={title}
        subnavTools={
          <Flex>
            {$companySettings.demobox.enabled && (
              <>
                <Button
                  minimal
                  icon="search-template"
                  text="Start Demo"
                  onClick={this.handleStartInvestigation}
                  mr={1}
                />
              </>
            )}
            {selectedToolResults?.length > 0 && (
              <>
                <Button
                  icon={TbLayoutSidebarRightCollapse}
                  text="Close Results"
                  minimal
                  onClick={this.handleCloseToolResults}
                />
                <Divider mx={1} />
              </>
            )}
            <Button minimal onClick={this.handleEmptyStart} loading={isCreatingJourney} icon="plus">
              <Text>New</Text>
            </Button>

            <Button
              minimal
              icon={BiHistory}
              text="History"
              active={!isSidebarCollapsed}
              onClick={this.handleSidebarCollapse}
              ml={1}
            />
          </Flex>
        }
      >
        {(loading || loadingStores) && this.renderLoading()}
        {!loading && !loadingStores && (
          <KaizenLayout
            gridTemplateColumns={isSidebarCollapsed ? '1fr 0px' : '1fr 375px'}
            gridTemplateRows={`minmax(200px, calc(100vh - ${NAV_HEIGHT}px))`}
            height="100%"
            mx={-3}
            gridRowGap={0}
            gridGap={0}
            bg="decks.background"
          >
            <ChatLayout
              gridTemplateRows={`minmax(200px, calc(100vh - ${INPUT_HEIGHT}px)) min-content`}
              gridTemplateColumns={selectedToolResults?.length ? '1fr 1fr' : '1fr'}
              gridGap={0}
              gridRowGap={0}
              width="100%"
              height="100%"
              pt={0}
              overflowY="scroll"
              bg="decks.background"
            >
              <KaizenScrollArea ref={this.scrollAreaRef} onScroll={this.handleScroll}>
                {containsQueries ? (
                  <Flex className="kaizen-chat-wrapper" flexDirection="column" alignItems="center" gap={4} flex={1}>
                    <KaizenChatSession
                      key={$journeys.chatSessionCollection?.selected.id}
                      chatSessionModel={$journeys.chatSessionCollection?.selected}
                      scrollToBottom={this.scrollToBottom}
                      isScrolledToBottom={isScrolledToBottom}
                      onSelectToolResult={this.handleSelectToolResult}
                      isExistingChatLoading={isExistingChatLoading}
                    />
                  </Flex>
                ) : (
                  <>
                    <Flex
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      gap="6px"
                      minWidth={750}
                      flex={1}
                    >
                      <LargeHeading>AI Advisor</LargeHeading>
                      <FancyInput mt={3}>
                        <KaizenInput
                          inputRef={this.inputRef}
                          key={selectedJourney?.id || 'new-journey-input'} // Ensure key changes if journey changes or is new
                          placeholder="Ask anything about your network"
                          createJourney={this.handleCreateJourney}
                          scrollToBottom={this.scrollToBottom}
                          selectJourneyAfterCreation={this.handleSelectJourneyAfterCreation} // Pass the new handler
                        />
                      </FancyInput>
                    </Flex>
                  </>
                )}
              </KaizenScrollArea>
              {selectedToolResults?.length > 0 && (
                <KaizenToolResultPanel
                  tools={selectedToolResults}
                  onCloseToolResult={this.handleCloseToolResult}
                  onClose={this.handleCloseToolResults}
                />
              )}
              {containsQueries && (
                <FancyInput>
                  <KaizenInput
                    scrollToBottom={this.scrollToBottom}
                    inputRef={this.inputRef}
                    key={selectedJourney?.id || 'new-journey-input'} // Ensure key changes if journey changes or is new
                    placeholder="Ask any question about your network"
                    chatSessionModel={$journeys.chatSessionCollection?.selected}
                    createJourney={this.handleCreateJourney}
                    selectJourneyAfterCreation={this.handleSelectJourneyAfterCreation} // Pass the new handler
                  />
                  <Flex alignItems="center" gap={2} role="contentinfo">
                    {containsQueries && (
                      <Button icon="arrow-down" onClick={this.scrollToBottom} small minimal>
                        <Text muted>Scroll to Bottom</Text>
                      </Button>
                    )}
                    <Text small muted>
                      Even AI has off days. Please verify important information.
                    </Text>
                  </Flex>
                </FancyInput>
              )}
            </ChatLayout>

            <KaizenSidebar borderLeft="thin" isCollapsed={isSidebarCollapsed}>
              <InputGroup
                autoFocus
                leftIcon="search"
                placeholder="Search..."
                value={searchInputValue}
                onChange={this.handleSearchInputChange}
                type="search"
              />

              <Flex flexDirection="column" gap={1}>
                <Button
                  textAlign="left"
                  fill
                  icon={isMyJourneysOpen ? 'caret-down' : 'caret-right'}
                  minimal
                  onClick={this.handleToggleMyJourneys}
                >
                  <SmallHeading>Recent</SmallHeading>
                </Button>
                <Collapse isOpen={isMyJourneysOpen}>
                  <KaizenChatList
                    onSelectJourney={this.handleSelectJourney}
                    hideUserName
                    hideShareLevel
                    hideEditedDate={false}
                    journeys={$journeys.chatSessionCollection.userChats}
                    maxInitialItems={10}
                  />
                </Collapse>
              </Flex>

              <Flex flexDirection="column" gap={1}>
                <Button
                  textAlign="left"
                  fill
                  icon={isCompanyJourneysOpen ? 'caret-down' : 'caret-right'}
                  minimal
                  onClick={this.handleToggleCompanyJourneys}
                >
                  <SmallHeading>Company</SmallHeading>
                </Button>
                <Collapse isOpen={isCompanyJourneysOpen}>
                  <KaizenChatList
                    onSelectJourney={this.handleSelectJourney}
                    hideEditedDate
                    hideShareLevel
                    journeys={$journeys.chatSessionCollection.companyChats}
                    maxInitialItems={10}
                  />
                </Collapse>
              </Flex>
              {isSudoer ? (
                <Button alignItems="center" justifyContent="center" onClick={this.handleViewPrompt}>
                  <Text>
                    <SudoTag fontSize={11} /> View system prompt
                  </Text>
                </Button>
              ) : null}
            </KaizenSidebar>
          </KaizenLayout>
        )}
      </Page>
    );
  }
}

export default KaizenMain;
